import type { CSSProperties } from "react";
import type { ProductCategory, ProductUnit } from "@/features/product/types";
import {
  CATEGORY_OPTIONS,
  UNIT_OPTIONS,
  UNIT_OPTION_LABELS,
  btnDanger,
  btnPrimary,
  inputStyle,
  labelStyle,
  selectStyle,
} from "./quotationDetailShared";

type InlineCreateProductPanelProps = {
  name: string;
  category: ProductCategory;
  unit: ProductUnit;
  categoryLabels: Record<string, string>;
  pending: boolean;
  onNameChange: (value: string) => void;
  onCategoryChange: (value: ProductCategory) => void;
  onUnitChange: (value: ProductUnit) => void;
  onCreate: () => void;
  onCancel: () => void;
};

type InlineCreateSupplierPanelProps = {
  name: string;
  pending: boolean;
  onNameChange: (value: string) => void;
  onCreate: () => void;
  onCancel: () => void;
};

const panelStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  padding: "0.75rem",
  background: "var(--login-bg)",
  border: "1px solid var(--login-border)",
  borderRadius: "0.5rem",
};

const compactPrimary = {
  ...btnPrimary,
  padding: "0.5rem 0.75rem",
  fontSize: "0.875rem",
} satisfies CSSProperties;

const compactDanger = {
  ...btnDanger,
  padding: "0.5rem 0.75rem",
  fontSize: "0.875rem",
} satisfies CSSProperties;

export function InlineCreateProductPanel({
  name,
  category,
  unit,
  categoryLabels,
  pending,
  onNameChange,
  onCategoryChange,
  onUnitChange,
  onCreate,
  onCancel,
}: InlineCreateProductPanelProps) {
  return (
    <div style={panelStyle}>
      <p
        style={{
          ...labelStyle,
          margin: 0,
          color: "var(--login-accent)",
        }}
      >
        Novo produto
      </p>
      <input
        style={inputStyle}
        value={name}
        onChange={(event) => onNameChange(event.target.value)}
        placeholder="Nome do produto"
      />
      <select
        style={selectStyle}
        value={category}
        onChange={(event) =>
          onCategoryChange(event.target.value as ProductCategory)
        }
      >
        {CATEGORY_OPTIONS.map((value) => (
          <option key={value} value={value}>
            {categoryLabels[value]}
          </option>
        ))}
      </select>
      <select
        style={selectStyle}
        value={unit}
        onChange={(event) => onUnitChange(event.target.value as ProductUnit)}
      >
        {UNIT_OPTIONS.map((value) => (
          <option key={value} value={value}>
            {UNIT_OPTION_LABELS[value]}
          </option>
        ))}
      </select>
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button
          type="button"
          onClick={onCreate}
          disabled={!name.trim() || pending}
          style={compactPrimary}
        >
          Criar
        </button>
        <button type="button" onClick={onCancel} style={compactDanger}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

export function InlineCreateSupplierPanel({
  name,
  pending,
  onNameChange,
  onCreate,
  onCancel,
}: InlineCreateSupplierPanelProps) {
  return (
    <div style={panelStyle}>
      <p
        style={{
          ...labelStyle,
          margin: 0,
          color: "var(--login-accent)",
        }}
      >
        Novo fornecedor
      </p>
      <input
        style={inputStyle}
        value={name}
        onChange={(event) => onNameChange(event.target.value)}
        placeholder="Nome do fornecedor"
      />
      <div style={{ display: "flex", gap: "0.5rem" }}>
        <button
          type="button"
          onClick={onCreate}
          disabled={!name.trim() || pending}
          style={compactPrimary}
        >
          Criar
        </button>
        <button type="button" onClick={onCancel} style={compactDanger}>
          Cancelar
        </button>
      </div>
    </div>
  );
}
